import { computed } from 'vue'
import { useStore } from 'vuex'

/**
 * useNumberFormatter composable
 * Centralized number and currency formatting based on user settings.
 * All formatters are reactive and update when locale settings change.
 */
export function useNumberFormatter() {
  const store = useStore()

  const locale = computed(() => store.state.settings?.general?.locale || 'en-US')
  const numberFormatSettings = computed(() => store.state.settings?.general?.numberFormat || {
    decimalPlaces: 2,
    maxDecimalPlaces: 4,
    currency: 'USD',
    currencyDecimals: 2,
    maxCurrencyDecimals: 4,
    useGrouping: true
  })

  const formatNumber = (value, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    const requestedDecimals = options.decimalPlaces ?? numberFormatSettings.value.decimalPlaces
    const decimals = Math.min(requestedDecimals, numberFormatSettings.value.maxDecimalPlaces)
    const useGrouping = options.useGrouping ?? numberFormatSettings.value.useGrouping

    return new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping
    }).format(num)
  }

  const formatCurrency = (value, currency = numberFormatSettings.value.currency, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    const requestedDecimals = options.decimalPlaces ?? numberFormatSettings.value.currencyDecimals
    const decimals = Math.min(requestedDecimals, numberFormatSettings.value.maxDecimalPlaces)
    const showSymbol = options.showSymbol !== false // default true

    try {
      return new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        currencyDisplay: showSymbol ? 'symbol' : 'code'
      }).format(num)
    } catch {
      // Intl throws RangeError on anything that is not a valid ISO 4217 code, and the
      // currency column holds free text in places ("EURO", "AMERIKAN DOLARI"). Thrown
      // from a template this takes the whole page down with a blank screen, so a bad
      // code degrades to the plain number followed by whatever was stored.
      const amount = new Intl.NumberFormat(locale.value, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(num)
      return currency ? `${amount} ${currency}` : amount
    }
  }

  // Additional formatting options
  const formatInteger = (value, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    const useGrouping = options.useGrouping ?? numberFormatSettings.value.useGrouping

    return new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping
    }).format(Math.round(num))
  }

  const formatDecimal = (value, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    const requestedDecimals = options.decimalPlaces ?? numberFormatSettings.value.decimalPlaces
    const decimals = Math.min(requestedDecimals, numberFormatSettings.value.maxDecimalPlaces)
    const useGrouping = options.useGrouping ?? numberFormatSettings.value.useGrouping

    return new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping
    }).format(num)
  }

  const formatPercentage = (value, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    const requestedDecimals = options.decimalPlaces ?? numberFormatSettings.value.decimalPlaces
    const decimals = Math.min(requestedDecimals, numberFormatSettings.value.maxDecimalPlaces)

    return new Intl.NumberFormat(locale.value, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num / 100)
  }

  const formatCompactNumber = (value, options = {}) => {
    const num = Number(value)
    if (Number.isNaN(num)) return ''

    return new Intl.NumberFormat(locale.value, {
      notation: 'compact',
      maximumFractionDigits: options.maximumFractionDigits ?? 1
    }).format(num)
  }

  // Get currency symbol for a given currency code
  const getCurrencySymbol = (currency = numberFormatSettings.value.currency) => {
    try {
      const formatter = new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
      const parts = formatter.formatToParts(0)
      const symbolPart = parts.find(part => part.type === 'currency')
      return symbolPart ? symbolPart.value : currency
    } catch (error) {
      return currency
    }
  }

  // Parse formatted number back to number
  const parseNumber = (formattedString) => {
    if (!formattedString) return 0
    
    // Get decimal and group separators from locale
    const formatter = new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })
    const parts = formatter.formatToParts(1234.5)
    const decimalPart = parts.find(part => part.type === 'decimal')
    const groupPart = parts.find(part => part.type === 'group')
    const decimalSeparator = decimalPart ? decimalPart.value : '.'
    const groupSeparator = groupPart ? groupPart.value : ','
    
    // Remove currency symbols and spaces
    let cleaned = formattedString.replace(/[^\d.,\s-]/g, '').trim()
    
    // Remove spaces (thousand separators in some locales)
    cleaned = cleaned.replace(/\s/g, '')
    
    // Handle negative sign
    const isNegative = cleaned.startsWith('-')
    cleaned = cleaned.replace(/^-/, '')
    
    // Replace group separators and normalize decimal separator
    const normalized = cleaned
      .replace(new RegExp('\\' + groupSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '')
      .replace(decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), '.')
    
    const parsed = parseFloat(normalized) || 0
    return isNegative ? -parsed : parsed
  }

  return { 
    formatNumber, 
    formatCurrency, 
    formatInteger,
    formatDecimal,
    formatPercentage,
    formatCompactNumber,
    getCurrencySymbol,
    parseNumber,
    locale, 
    numberFormatSettings 
  }
}


